/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import { useEffect } from 'react'

import { WidgetGuard } from 'features/types'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { getMetricCategory } from 'utils/MetricItemUtil'
import { createMeasureMetric, createMetric } from 'utils/runSearchUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { getOptions } from 'utils/timelineUtil'
import { toFilter } from 'utils/widgetUtil'
import { MeasureType } from 'enums'
import { SocialWebTimelineWidget } from 'schemas/SocialWebTimelineWidgetSchema'
import { Dashboard } from 'schemas/DashboardSchema'
import { MetricOptions } from 'features/widget/types'
import { useAppDispatch } from 'app/hooks'
import { getTimelineAnnotationsByTopicId } from 'features/topicAnnotations/asyncThunks'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

const createSearches = (
  widgetSetting: SocialWebTimelineWidget,
  dashboard: Dashboard,
  pageConfigUser: any
) => {
  const {
    measureTypes = [],
    metricNames = [],
    viewOption,
    precision,
    selectedTopic
  } = widgetSetting

  const option: MetricOptions = {}

  if (measureTypes.length === 1 || metricNames.length) {
    option.metricCategory = getMetricCategory(viewOption)
  }

  const searches = []
  if (measureTypes.length) {
    if (
      measureTypes.includes(MeasureType.OVERALL_STAR_RATINGS_AVERAGE) ||
      measureTypes.includes(MeasureType.OVERALL_SURVEY_SCORES_AVERAGE)
    ) {
      option.smoothing = false
    }

    searches.push(
      createMetric(
        dashboard.id!,
        dashboard.name!,
        precision!,
        [selectedTopic.topicId!],
        toFilter(widgetSetting, pageConfigUser.timeZone),
        measureTypes.flatMap((measure) => getMetricSeries(measure!)),
        getOptions(widgetSetting),
        option
      )
    )
  }

  if (metricNames.length) {
    searches.push(
      createMeasureMetric(
        dashboard.id!,
        dashboard.name!,
        precision!,
        [selectedTopic.topicId!],
        toFilter(widgetSetting, pageConfigUser.timeZone),
        metricNames as string[],
        getOptions(widgetSetting),
        option
      )
    )
  }

  searches.push(
    createMetric(
      dashboard.id!,
      dashboard.name!,
      precision!,
      [selectedTopic.topicId!],
      toFilter(widgetSetting, pageConfigUser.timeZone),
      getMetricSeries(MeasureType.MENTIONS),
      getOptions(widgetSetting)
    )
  )

  return searches
}

const RunVOCSearchEffect: WidgetGuard = ({ widgetId, children }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebTimelineWidget>(widgetId)
  const dashboard = useDashboardData()
  const pageConfigUser = usePageConfigUser()
  const dispatch = useAppDispatch()

  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboard!, pageConfigUser)

    if (searches.length > 0) {
      searches.sort((a, b) => a.priority - b.priority)
      dispatch(getWidgetForDashboard({ searches, widgetId, timestamp: refreshTimestamp }))
    }
  }, [widgetId, dispatch, refreshTimestamp, widgetSetting, dashboard, pageConfigUser])

  useEffect(() => {
    dispatch(getTimelineAnnotationsByTopicId({ topicId: widgetSetting.topicInfos![0].topicId! }))
  }, [widgetSetting, dispatch])

  return children
}

export default RunVOCSearchEffect
